import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, Loader2, RefreshCw, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { showApiError, showError, showSuccess, showWarning } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import { companyEditConfirmPresentation } from '@/features/company/constants'
import {
  confirmYardiResidentMappings,
  fetchYardiPropertyContext,
  syncYardiResidents,
  updateYardiResidentMappingEligibility,
} from '../../services/yardiResidentService'
import {
  getYardiReviewExpandKey,
  getYardiReviewSelectionKey,
  isYardiReviewRowExpanded,
  nextYardiReviewExpandedKey,
} from '../../utils/yardiResidentReviewLogic'
import type {
  YardiMatchBucket,
  YardiResidentMatch,
  YardiResidentRecord,
  YardiResidentSyncPanelProps,
  YardiResidentSyncResponse,
  YardiResidentType,
  YardiSyncContext,
} from '../../types/yardiResidentSync'

type ConfidenceFilter = 'all' | 'selected' | 'selectable' | 'high' | 'medium' | 'low'

const REVIEW_BUCKETS: YardiMatchBucket[] = ['matched', 'review', 'skipped', 'notMappable', 'synced']

function bucketRows(response: YardiResidentSyncResponse, bucket: YardiMatchBucket): YardiResidentMatch[] {
  const matched = Array.isArray(response.matched) ? response.matched : []
  if (bucket === 'matched') return matched.filter((row) => String(row.decision).toUpperCase() !== 'MANUAL_REVIEW')
  if (bucket === 'review') return matched.filter((row) => String(row.decision).toUpperCase() === 'MANUAL_REVIEW')
  if (bucket === 'skipped') return Array.isArray(response.skipped) ? response.skipped : []
  if (bucket === 'notMappable') {
    const rows = response.notMappable ?? response.not_mappable
    return Array.isArray(rows) ? rows : []
  }
  const synced = response.alreadySynced ?? response.already_synced
  return Array.isArray(synced) ? synced : []
}

function libertyRecord(row: YardiResidentMatch): YardiResidentRecord {
  return (
    (row.liberty_matched_candidate as YardiResidentRecord | undefined) ??
    (row.liberty_main_candidate as YardiResidentRecord | undefined) ??
    row.liberty ??
    row.liberty_resident ??
    row
  )
}

function yardiRecord(row: YardiResidentMatch): YardiResidentRecord {
  return (
    (row.source_matched_candidate as YardiResidentRecord | undefined) ??
    (row.source_main_candidate as YardiResidentRecord | undefined) ??
    row.yardi ??
    row.yardi_resident ??
    row.selected_candidate ??
    row.candidate ??
    {}
  )
}

function recordName(record: YardiResidentRecord): string {
  return (
    record.name ||
    `${String(record.first_name ?? record.firstName ?? record.user_first_name ?? '')} ${String(record.last_name ?? record.lastName ?? record.user_last_name ?? '')}`.trim() ||
    'Unknown resident'
  )
}

function isSelectable(row: YardiResidentMatch): boolean {
  // Angular canSelectYardiReviewItem: requires exact case-sensitive 'MATCHED' status
  return row.liberty_entity_id != null && String(row.status ?? '') === 'MATCHED'
}

function hasLibertyEntityId(row: YardiResidentMatch): boolean {
  return row.liberty_entity_id != null && String(row.liberty_entity_id) !== ''
}

function hasRowCheckbox(row: YardiResidentMatch, bucket: YardiMatchBucket): boolean {
  if (bucket === 'skipped' || bucket === 'notMappable') return hasLibertyEntityId(row)
  if (bucket === 'matched' || bucket === 'review') return isSelectable(row)
  return false
}

function applyEligibilityLocally(
  response: YardiResidentSyncResponse,
  ids: Array<string | number>,
  status: 'MAPPABLE' | 'NOT_MAPPABLE',
): YardiResidentSyncResponse {
  const idSet = new Set(ids.map((id) => Number(id)).filter((id) => id > 0))
  const belongs = (row: YardiResidentMatch) => idSet.has(Number(row.liberty_entity_id))
  const matched = response.matched ?? []
  const skipped = response.skipped ?? []
  const notMappable = bucketRows(response, 'notMappable')

  if (status === 'NOT_MAPPABLE') {
    const moved = [...matched.filter(belongs), ...skipped.filter(belongs), ...notMappable.filter(belongs)].map(
      (row) => ({ ...row, status: 'NOT_MAPPABLE' }),
    )
    const seen = new Set<number>()
    const nextNotMappable: YardiResidentMatch[] = []
    for (const row of [...notMappable.filter((row) => !belongs(row)), ...moved]) {
      const id = Number(row.liberty_entity_id)
      if (seen.has(id)) continue
      seen.add(id)
      nextNotMappable.push(row)
    }
    return {
      ...response,
      matched: matched.filter((row) => !belongs(row)),
      skipped: skipped.filter((row) => !belongs(row)),
      notMappable: nextNotMappable,
    }
  }

  return {
    ...response,
    notMappable: notMappable.filter((row) => !belongs(row)),
    skipped: [...skipped, ...notMappable.filter(belongs).map((row) => ({ ...row, status: 'SKIPPED' }))],
  }
}

function confidence(row: YardiResidentMatch): 'high' | 'medium' | 'low' | 'none' {
  const breakdown = (row.score_breakdown ?? row.breakdown ?? {}) as Record<string, unknown>
  const supplied = String(
    row.confidence ??
      row.confidence_level ??
      row.confidence_tier ??
      breakdown.confidenceTier ??
      '',
  ).toLowerCase()
  if (supplied.includes('high')) return 'high'
  if (supplied.includes('medium')) return 'medium'
  if (supplied.includes('low')) return 'low'
  const score = Number(row.score ?? row.total_score ?? breakdown.totalScore ?? breakdown.total)
  if (Number.isFinite(score)) {
    if (score >= 110) return 'high'
    if (score >= 100) return 'low'
  }
  return 'none'
}

function searchableText(row: YardiResidentMatch): string {
  const records = [libertyRecord(row), yardiRecord(row)]
  return [
    row.decision,
    row.status,
    row.reason,
    row.message,
    ...records.flatMap((record) => [
      recordName(record),
      record.email,
      record.user_email,
      record.phone,
      record.mobile_no,
      record.unit,
      record.unit_number,
      record.applicant_id,
      record.resident_id,
      record.tCode,
      record.pCode,
      row.source_entity_id,
    ]),
  ]
    .filter((value) => value != null)
    .join(' ')
    .toLowerCase()
}

function displayDate(value: unknown): string {
  if (!value || String(value) === '--') return '--'
  const raw = String(value).trim()
  const usDate = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (usDate) {
    return `${usDate[1].padStart(2, '0')}/${usDate[2].padStart(2, '0')}/${usDate[3]}`
  }
  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s|$)/)
  if (isoDate && !raw.includes('T')) return `${isoDate[2]}/${isoDate[3]}/${isoDate[1]}`
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'America/New_York',
  }).format(date)
}

function decisionLabel(row: YardiResidentMatch): string {
  const status = String(row.status ?? '').toUpperCase()
  if (status === 'NOT_MAPPABLE' || status === 'UNMATCHABLE') return 'Permanently Skipped'
  if (row.decision === 'MANUAL_REVIEW') return 'Needs Review'
  if (row.decision === 'AUTO_MATCH') return 'Auto Match'
  return String(row.status ?? 'Skipped').replace(/_/g, ' ')
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'NA'
}

function recordPhone(record: YardiResidentRecord): string {
  const phones = record.phone
  if (Array.isArray(phones)) {
    const first = phones[0] as Record<string, unknown> | undefined
    if (first?.phoneNumber) return String(first.phoneNumber)
    return '--'
  }
  if (typeof phones === 'string' && phones.trim()) return phones
  return String(record.mobile_no ?? record.user_mobile_no ?? '--')
}

function ResidentDetailCard({
  row,
  source,
}: {
  row: YardiResidentMatch
  source?: boolean
}) {
  const record = source ? yardiRecord(row) : libertyRecord(row)
  const raw = (record.raw ?? {}) as Record<string, unknown>
  const sourceMain = (row.source_main_candidate ?? {}) as YardiResidentRecord
  const sourceRole = String(record.sourceRole ?? record.source_role ?? '')
  const residentType = source
    ? sourceRole === 'PRIMARY'
      ? 'Primary'
      : sourceRole === 'CO_APPLICANT'
        ? 'Co Applicant'
        : '--'
    : record.applicant_id && record.primary_applicant_id
      ? String(record.applicant_id) === String(record.primary_applicant_id)
        ? 'Primary'
        : 'Co Applicant'
      : '--'
  const fields = source
    ? [
        ['Phone', recordPhone(record)],
        ['Unit', record.unitCode ?? record.unit_number ?? '--'],
        ['Lease Start', displayDate(record.leaseStartDate ?? record.leaseFromDate)],
        ['Move In', displayDate(record.moveInDate ?? record.move_in_date ?? sourceMain.moveInDate)],
        ['Resident Type', residentType],
        ['Resident TCode', row.source_entity_id ?? record.tCode ?? '--'],
        ['Resident PCode', raw.pCode ?? record.pCode ?? '--'],
        ['Resident Status', raw.scope ?? '--'],
      ]
    : [
        ['Phone', recordPhone(record)],
        ['Unit', record.unit_number ?? record.unit ?? '--'],
        ['Lease Start', displayDate(record.lease_start_date)],
        ['Resident Type', residentType],
        ['Resident ID', row.liberty_entity_id ?? '--'],
        ['Applicant ID', record.applicant_id ?? '--'],
        ['Application ID', record.application_id ?? '--'],
      ]
  return (
    <div className={cn('rounded-[18px] border border-[#e4ebf7] bg-white p-5 shadow-sm', !source && 'bg-gradient-to-b from-[#f7fbff] to-white')}>
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">
        {source ? 'Yardi Resident Record' : 'Liberty Resident'}
      </p>
      <h3 className="mb-1 text-lg font-bold text-[#16213e]">{recordName(record)}</h3>
      <p className="mb-4 text-xs text-[#627089]">{String(record.email ?? record.user_email ?? '--')}</p>
      <dl className="space-y-1 text-xs leading-6 text-[#51607a]">
        {fields.map(([label, value]) => (
          <div key={String(label)} className="flex gap-1">
            <dt>{String(label)}:</dt>
            <dd className="break-words">{String(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function MatchEvidence({ row }: { row: YardiResidentMatch }) {
  const primary = (row.primary_evaluation ?? {}) as Record<string, unknown>
  const bestCandidate = (primary.bestCandidate ?? {}) as Record<string, unknown>
  const breakdown = (row.score_breakdown ??
    row.breakdown ??
    bestCandidate.breakdown ??
    {}) as Record<string, unknown>
  const entries = [
    { label: 'Email', key: 'emailPoints', value: Number(breakdown.emailPoints) || 0, max: 70 },
    { label: 'Phone', key: 'phonePoints', value: Number(breakdown.phonePoints) || 0, max: 45 },
    { label: 'Name', key: 'namePoints', value: Number(breakdown.namePoints) || 0, max: 25 },
    { label: 'Unit', key: 'unitPoints', value: Number(breakdown.unitPoints) || 0, max: 20 },
    { label: 'Lease', key: 'leasePoints', value: Number(breakdown.leasePoints) || 0, max: 25 },
    { label: 'Source', key: 'sourcePoints', value: Number(breakdown.sourcePoints) || 0, max: 5 },
    { label: 'Unit Penalty', key: 'unitPenalty', value: Math.abs(Number(breakdown.unitPenalty) || 0), max: 10 },
    {
      label: 'Lease Penalty',
      key: 'leasePenalty',
      value: Math.abs(Number(breakdown.leasePenalty) || 0),
      max: Math.max(Math.abs(Number(breakdown.leasePenalty) || 0), 20),
    },
    {
      label: 'Move-out Penalty',
      key: 'moveOutPenalty',
      value: Math.abs(Number(breakdown.moveOutPenalty) || 0),
      max: Math.max(Math.abs(Number(breakdown.moveOutPenalty) || 0), 20),
    },
  ]
  const sourceRanked = Array.isArray(row.source_ranked_candidates) ? row.source_ranked_candidates : []
  const libertyRanked = Array.isArray(row.liberty_ranked_candidates) ? row.liberty_ranked_candidates : []
  const yardiRanked = Array.isArray(row.yardi_ranked_candidates) ? row.yardi_ranked_candidates : []
  const ranked = (sourceRanked.length ? sourceRanked : libertyRanked.length ? libertyRanked : yardiRanked) as Array<
    Record<string, unknown>
  >
  const matchReason = row.match_reason ?? breakdown.matchReason

  return (
    <div className="mt-5 space-y-5">
      {matchReason ? (
        <p className="text-[11px] text-[#70809b]">
          <span className="font-semibold">Reason:</span> {String(matchReason)}
        </p>
      ) : null}
      {entries.some((entry) => entry.value > 0) ? (
        <div>
          <h4 className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">Candidate Ranking &amp; Score Breakdown</h4>
          <div className="grid gap-4 rounded-[18px] border border-[#e4ebf7] bg-white p-[18px] sm:grid-cols-2">
            {entries.map(({ label, key, value, max }) => {
              return (
                <div key={key}>
                  <div className="mb-2 flex justify-between text-[11px] text-[#51607a]">
                    <span>{label}</span>
                    <strong>{value}/{max}</strong>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-[#e8eef9]">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#27b36a] to-[#59cf86]"
                      style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}
      {ranked.length ? (
        <div>
          <h4 className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">Top Ranked Yardi Residents</h4>
          <div className="space-y-4">
            {ranked.slice(0, 2).map((rank, index) => {
              const candidate = (rank.candidate ?? rank) as Record<string, unknown>
              const resident = (candidate.resident ?? {}) as YardiResidentRecord
              const subject = (candidate.subject ?? candidate.resident ?? candidate) as YardiResidentRecord
              const candidateBreakdown = (rank.breakdown ?? candidate.breakdown ?? {}) as Record<string, unknown>
              const candidateSignals = entries.map(({ label, key, max }) => {
                const value = key.toLowerCase().includes('penalty')
                  ? Math.abs(Number(candidateBreakdown[key]) || 0)
                  : Number(candidateBreakdown[key]) || 0
                return {
                  label,
                  key,
                  value,
                  max:
                    key === 'leasePenalty' || key === 'moveOutPenalty'
                      ? Math.max(value, 20)
                      : max,
                }
              })
              const applicantRecords = candidate.resident && candidate.subject && subject.sourceRole === 'CO_APPLICANT'
                ? [
                    ['Primary Applicant', resident],
                    ['Co-Applicant', subject],
                  ] as const
                : [['Resident', subject]] as const
              return (
                <div key={index} className="rounded-[18px] border border-[#e4ebf7] bg-white p-[18px]">
                  <div className="mb-3 flex justify-between text-[11px] font-bold text-[#51607a]">
                    <span>Candidate {index + 1}</span>
                    <strong>{String(rank.totalScore ?? candidate.totalScore ?? '--')}/190</strong>
                  </div>
                  <div className={cn('grid gap-3', applicantRecords.length > 1 && 'sm:grid-cols-2')}>
                    {applicantRecords.map(([label, applicant]) => (
                      <div key={label} className="rounded-xl border border-[#e4ebf7] bg-[#f8faff] p-3.5">
                        <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-[#51607a]">{label}</p>
                        <h5 className="font-bold text-[#16213e]">{recordName(applicant)}</h5>
                        <p className="text-xs text-[#70809b]">{String(applicant.email ?? '--')}</p>
                        <p className="mt-2 text-xs leading-5 text-[#51607a]">
                          Phone: {recordPhone(applicant)}<br />
                          Unit: {String(applicant.unitCode ?? '--')}<br />
                          Lease Start: {displayDate(applicant.leaseStartDate)}<br />
                          Move In: {displayDate(applicant.moveInDate)}<br />
                          Resident TCode: {String(applicant.tCode ?? applicant.sourceEntityId ?? '--')}<br />
                          Resident PCode: {String(applicant.pCode ?? '--')}
                        </p>
                      </div>
                    ))}
                  </div>
                  {(rank.match_reason ?? candidateBreakdown.matchReason) ? (
                    <p className="mt-3 text-xs text-[#70809b]">{String(rank.match_reason ?? candidateBreakdown.matchReason)}</p>
                  ) : null}
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {candidateSignals.map((signal) => (
                      <div key={signal.key}>
                        <div className="mb-1 flex justify-between text-[10px] text-[#51607a]">
                          <span>{signal.label}</span>
                          <strong>{signal.value}/{signal.max}</strong>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-[#e8eef9]">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-[#27b36a] to-[#59cf86]"
                            style={{ width: `${Math.min((signal.value / signal.max) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function YardiResidentSyncPanel({
  propertyId,
  companyId,
  propertyCode,
  propertyName,
  residentId,
  onConfirmed,
  refreshContextBeforeRequest = false,
}: YardiResidentSyncPanelProps) {
  const [residentType, setResidentType] = useState<YardiResidentType | ''>('')
  const [syncing, setSyncing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [skipping, setSkipping] = useState(false)
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false)
  const [mappingMessage, setMappingMessage] = useState('')
  const [response, setResponse] = useState<YardiResidentSyncResponse | null>(null)
  const [syncContext, setSyncContext] = useState<YardiSyncContext | null>(null)
  const [activeBucket, setActiveBucket] = useState<YardiMatchBucket>('matched')
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<ConfidenceFilter>('all')
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  useEffect(() => {
    if (!response) return
    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
    }
  }, [response])

  const counts = useMemo(
    () => ({
      matched: response ? bucketRows(response, 'matched').length : 0,
      review: response ? bucketRows(response, 'review').length : 0,
      skipped: response ? bucketRows(response, 'skipped').length : 0,
      notMappable: response ? bucketRows(response, 'notMappable').length : 0,
      synced: response ? bucketRows(response, 'synced').length : 0,
    }),
    [response],
  )
  const totalResidents = response
    ? counts.matched + counts.review + counts.skipped + counts.notMappable + counts.synced
    : 0

  const activeRows = useMemo(() => (response ? bucketRows(response, activeBucket) : []), [activeBucket, response])
  const visibleRows = useMemo(
    () =>
      activeRows.filter((row) => {
        const selectionKey = getYardiReviewSelectionKey(row)
        if (query.trim() && !searchableText(row).includes(query.trim().toLowerCase())) return false
        if (filter === 'selected') return selectedKeys.has(selectionKey)
        if (filter === 'selectable') return hasRowCheckbox(row, activeBucket)
        if (['high', 'medium', 'low'].includes(filter)) return confidence(row) === filter
        return true
      }),
    [activeBucket, activeRows, filter, query, selectedKeys],
  )

  const startSync = async () => {
    if (!residentType) {
      showWarning('Select Current or Past residents before syncing.')
      return
    }
    setSyncing(true)
    try {
      let resolvedCompanyId = companyId ?? ''
      let resolvedPropertyCode = propertyCode?.trim() ?? ''
      if (refreshContextBeforeRequest || !resolvedCompanyId || !resolvedPropertyCode) {
        const propertyContext = await fetchYardiPropertyContext(propertyId)
        resolvedCompanyId ||= propertyContext.companyId
        resolvedPropertyCode ||= propertyContext.propertyCode.trim()
      }
      // Angular splits these into distinct guards (missing yardi_code, then missing company)
      if (!resolvedPropertyCode) {
        showError('Yardi Code for this property is required for syncing residents!')
        return
      }
      if (!resolvedCompanyId) {
        showError('Company information is not available for this property.')
        return
      }
      const context: YardiSyncContext = {
        property_id: propertyId,
        resident_type: residentType,
        company_id: resolvedCompanyId,
        property_code: resolvedPropertyCode,
        resident_id: (residentId ?? null) as YardiSyncContext['resident_id'],
        resident_default_id: (residentId ?? null) as YardiSyncContext['resident_default_id'],
      }
      const result = await syncYardiResidents(context)
      const totalResidents =
        Number(result.summary?.totalResidents) ||
        REVIEW_BUCKETS.reduce((total, bucket) => total + bucketRows(result, bucket).length, 0)
      if (!totalResidents) {
        // Angular: 'No Yardi residents found for review.'
        showError('No Yardi residents found for review.')
        return
      }
      setSyncContext(context)
      setResponse(result)
      setMappingMessage('')
      setSelectedKeys(new Set())
      setExpandedKey(null)
      const firstBucket = REVIEW_BUCKETS.find((bucket) => bucketRows(result, bucket).length > 0)
      setActiveBucket(firstBucket ?? 'matched')
    } catch (error) {
      showApiError(error)
    } finally {
      setSyncing(false)
    }
  }

  const selectableVisible = visibleRows
    .map((row) => ({ row, key: getYardiReviewSelectionKey(row) }))
    .filter(({ row }) => hasRowCheckbox(row, activeBucket))
  const allVisibleSelected =
    selectableVisible.length > 0 && selectableVisible.every(({ key }) => selectedKeys.has(key))
  const expandedRow =
    visibleRows.find((row, index) => isYardiReviewRowExpanded(expandedKey, row, index)) ?? null
  useEffect(() => {
    if (expandedKey && !expandedRow) setExpandedKey(null)
  }, [expandedKey, expandedRow])

  const submitMappings = async () => {
    if (!response || !syncContext) return
    const candidates = [...bucketRows(response, 'matched'), ...bucketRows(response, 'review')]
    const selected = candidates.filter((row) => selectedKeys.has(getYardiReviewSelectionKey(row)))
    if (!selected.length) {
      showWarning('Select at least one resident mapping.')
      return
    }
    setConfirming(true)
    try {
      const result = await confirmYardiResidentMappings(syncContext, selected)
      const resultObject =
        result && typeof result === 'object' ? (result as Record<string, unknown>) : {}
      const mapped = Array.isArray(resultObject.mapped) ? resultObject.mapped : []
      const skipped = Array.isArray(resultObject.skipped) ? resultObject.skipped : []
      if (!mapped.length) {
        const first = skipped[0] as Record<string, unknown> | undefined
        const message = String(first?.reason ?? first?.message ?? 'Unable to save mapping.')
        setMappingMessage(message)
        showError(message)
        return
      }
      // Angular getYardiMappingResponseMessage: any mapped -> plain success regardless of skipped
      showSuccess('Yardi resident mappings updated successfully.')
      closeReview()
      await onConfirmed?.()
    } catch (error) {
      showApiError(error)
    } finally {
      setConfirming(false)
    }
  }

  const selectedSkipRows = useMemo(() => {
    if (!response) return []
    const source =
      activeBucket === 'notMappable'
        ? bucketRows(response, 'notMappable')
        : [...bucketRows(response, 'matched'), ...bucketRows(response, 'review'), ...bucketRows(response, 'skipped')]
    return source.filter((row) => selectedKeys.has(getYardiReviewSelectionKey(row)) && hasLibertyEntityId(row))
  }, [activeBucket, response, selectedKeys])

  const markPermanentSkip = async () => {
    if (!response || !syncContext || !selectedSkipRows.length) {
      showWarning(
        activeBucket === 'notMappable'
          ? 'Select at least one permanently skipped resident.'
          : 'Select at least one resident to mark as permanently skipped.',
      )
      return
    }
    const status = activeBucket === 'notMappable' ? 'MAPPABLE' : 'NOT_MAPPABLE'
    const ids = selectedSkipRows
      .map((row) => Number(row.liberty_entity_id))
      .filter((id) => Number.isFinite(id) && id > 0)
    setSkipping(true)
    try {
      await updateYardiResidentMappingEligibility(syncContext, status, ids)
      setResponse(applyEligibilityLocally(response, ids, status))
      setSelectedKeys(new Set())
      setActiveBucket(status === 'NOT_MAPPABLE' ? 'notMappable' : 'skipped')
      showSuccess(
        status === 'NOT_MAPPABLE'
          ? 'Residents marked as permanently skipped and will be excluded from the next sync.'
          : 'Residents restored and can be reviewed again.',
      )
    } catch (error) {
      showApiError(error)
    } finally {
      setSkipping(false)
    }
  }

  const closeReview = () => {
    setResponse(null)
    setActiveBucket('matched')
    setQuery('')
    setFilter('all')
    setSelectedKeys(new Set())
    setExpandedKey(null)
    setSubmitConfirmOpen(false)
    setMappingMessage('')
  }

  return (
    <>
      <div className="space-y-4" data-testid="yardi-resident-sync-panel">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Sync Yardi Residents</h3>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="block w-full max-w-[280px] text-sm font-medium text-slate-700">
            Resident Type
            {/* appearance-none so the rounded border renders (native selects ignore it)
                and a custom chevron keeps padding/alignment consistent. */}
            <div className="relative mt-1">
              <select
                value={residentType}
                onInput={(event) =>
                  setResidentType(event.currentTarget.value as YardiResidentType | '')
                }
                onChange={(event) =>
                  setResidentType(event.currentTarget.value as YardiResidentType | '')
                }
                className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-white pl-3 pr-10 text-sm outline-none transition-colors focus:border-[rgb(21_151_255)] focus:ring-2 focus:ring-[rgb(21_151_255)]/15"
                data-testid="yardi-resident-type-select"
              >
                <option value="">Select resident type…</option>
                <option value="current">Current Residents</option>
                <option value="past">Past Residents</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </label>
          <button
            type="button"
            onClick={() => void startSync()}
            disabled={!residentType || syncing}
            className={cn(companyFormStyles.btnPrimary, 'h-10 min-w-[150px] gap-2')}
            data-testid="yardi-resident-sync-button"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {syncing ? 'Syncing…' : 'Sync Residents'}
          </button>
        </div>
      </div>

      {response ? (
        <div className="fixed inset-0 z-[10020] flex items-center justify-center p-3 sm:p-6" data-testid="yardi-resident-review-modal">
          <div className="absolute inset-0 bg-[#091430]/55 backdrop-blur-[3px]" aria-hidden />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="yardi-review-title"
            className="relative flex max-h-[calc(100vh-24px)] w-[92vw] max-w-full flex-col overflow-hidden rounded-[10px] bg-gradient-to-b from-[#fbfcff] to-[#f3f7ff] text-[13px] leading-[1.45] text-[#51607a] shadow-[0_28px_70px_rgba(15,23,42,0.2)] sm:max-h-[calc(100vh-48px)]"
          >
          <header className="flex items-start justify-between gap-4 border-b border-[#e5edf8] bg-white px-5 py-[18px] sm:px-7">
            <div className="flex min-w-0 flex-1 flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="min-w-[220px]">
                <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#5f6fff]">Decision Matrix</p>
                <h2 id="yardi-review-title" className="text-[22px] font-bold text-[#16213e]">Resident Match Review</h2>
              </div>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                {[
                  ['Matched', counts.matched, 'from-[#f3fff6]'],
                  ['Review Needed', counts.review, 'from-[#f3fff6]'],
                  ['Skipped', counts.skipped, 'from-[#fff1f2]'],
                  ['Permanently Skipped', counts.notMappable, 'from-[#f4f6fa]'],
                  ['Already Synced', counts.synced, 'from-[#fff9eb]'],
                  ['Total', totalResidents, 'from-[#f4f0ff]'],
                ].map(([label, count, tone]) => (
                  <div key={String(label)} className={cn('min-w-0 rounded-[14px] border border-[#e4ebf7] bg-gradient-to-b to-white px-2 py-3 shadow-sm sm:min-w-[100px] sm:px-3', tone)}>
                    <span className="block text-[10px] leading-tight text-[#6f7c93]">{String(label)}</span>
                    <strong className="mt-1 block text-lg leading-none text-[#16213e]">{String(count)}</strong>
                  </div>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={closeReview}
              className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full border border-[#d5e0f1] bg-white text-[#445168]"
              aria-label="Close resident match review"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {mappingMessage ? (
              <div className="mx-5 mt-3 rounded-lg border border-[#f5d98c] bg-[#fff8e6] px-3.5 py-2.5 text-sm font-semibold text-[#8a5a00] sm:mx-7">
                {mappingMessage}
              </div>
            ) : null}
            <div className="flex flex-col gap-3 px-5 py-2.5 sm:px-7 xl:flex-row xl:items-start xl:justify-between">
              <div className="flex flex-wrap gap-2 pt-1.5">
                <span className="rounded-full border border-[#dfe8f5] bg-white px-3 py-[7px] text-xs font-semibold">{propertyName?.trim() || 'Property'}</span>
                <span className="rounded-full border border-[#dfe8f5] bg-white px-3 py-[7px] text-xs font-semibold">
                  Yardi Candidate Pool: {residentType === 'past' ? 'Past Residents' : 'Current Residents'}
                </span>
                <span className="rounded-full border border-[#dfe8f5] bg-[#eef3ff] px-3 py-[7px] text-xs font-semibold text-[#3151d3]">{selectedKeys.size} selected</span>
                <span className="rounded-full border border-[#dfe8f5] bg-white px-3 py-[7px] text-xs font-semibold">{visibleRows.length} visible</span>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <label className="relative w-full sm:w-[360px]">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search by resident, email, unit, or code..."
                    className="h-[42px] w-full rounded-[14px] border border-[#d7e2f2] bg-white pl-9 pr-3 text-sm outline-none"
                  />
                </label>
                <select
                  value={filter}
                  onChange={(event) => setFilter(event.target.value as ConfidenceFilter)}
                  className="h-[42px] w-full rounded-[14px] border border-[#d7e2f2] bg-white px-3 text-sm sm:w-[220px]"
                >
                  <option value="all">All Visible Results</option>
                  <option value="selected">Selected Only</option>
                  <option value="selectable">Selectable Only</option>
                  <option value="high">High Confidence</option>
                  <option value="medium">Medium Confidence</option>
                  <option value="low">Low Confidence</option>
                </select>
                {visibleRows.length ? (
                  <label className="flex whitespace-nowrap text-xs font-semibold text-[#51607a]">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={allVisibleSelected}
                      disabled={!selectableVisible.length}
                      onChange={(event) => {
                        const checked = event.target.checked
                        setSelectedKeys((current) => {
                          const next = new Set(current)
                          selectableVisible.forEach(({ key }) => {
                            if (checked) next.add(key)
                            else next.delete(key)
                          })
                          return next
                        })
                      }}
                    />
                    Select all visible
                  </label>
                ) : null}
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col px-5 pb-6 sm:px-7">
              <div className="mb-0.5 mt-[15px] flex shrink-0 flex-wrap gap-3 border-b border-[#e4ebf7] pb-[15px]">
                {(
                  [
                    ['matched', 'Matched'],
                    ['review', 'Needs Review'],
                    ['skipped', 'Skipped'],
                    ['notMappable', 'Permanently Skipped'],
                    ['synced', 'Synced'],
                  ] as Array<[YardiMatchBucket, string]>
                ).map(([bucket, label]) => (
                  <button
                    key={bucket}
                    type="button"
                    onClick={() => {
                      setActiveBucket(bucket)
                      setExpandedKey(null)
                    }}
                    className={cn(
                      'rounded-xl border px-3.5 py-2.5 text-xs font-bold',
                      activeBucket === bucket
                        ? 'border-[#1597ff] bg-[#1597ff] text-white shadow-[0_12px_24px_rgba(49,81,211,0.28)]'
                        : 'border-[#d7e2f2] bg-white text-[#5b6780]',
                    )}
                  >
                    {label} ({counts[bucket]})
                  </button>
                ))}
                <button
                  type="button"
                  disabled={!selectedSkipRows.length || skipping || confirming}
                  onClick={() => void markPermanentSkip()}
                  className="inline-flex h-10 min-w-[200px] items-center justify-center gap-2 rounded-md border border-[#d7e2f2] bg-white px-4 text-sm font-semibold text-[#445168] sm:ml-auto"
                  data-testid="yardi-resident-mark-permanent-skip"
                >
                  {skipping ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {activeBucket === 'notMappable'
                    ? `Mark Mappable (${selectedSkipRows.length})`
                    : `Mark Permanent Skip (${selectedSkipRows.length})`}
                </button>
                <button
                  type="button"
                  disabled={!selectedKeys.size || confirming || skipping}
                  onClick={() => setSubmitConfirmOpen(true)}
                  className={cn(companyFormStyles.btnPrimary, 'w-full min-w-[220px] gap-2 sm:w-auto')}
                >
                  {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Map Selected Residents ({selectedKeys.size})
                </button>
              </div>

              <div className="flex min-h-0 flex-1 overflow-hidden">
                <div className="min-h-0 max-h-[530px] min-w-0 flex-1 overflow-y-auto rounded-[20px] bg-white p-3">
                  {visibleRows.length ? (
                    visibleRows.map((row, index) => {
                      const selectionKey = getYardiReviewSelectionKey(row)
                      const expandKey = getYardiReviewExpandKey(row, index)
                      const liberty = libertyRecord(row)
                      const expanded = isYardiReviewRowExpanded(expandedKey, row, index)
                      const score = row.score ?? row.total_score
                      return (
                        <div
                          key={expandKey}
                          className={cn(
                            'border-b border-[#cccccc6b] px-4 py-4 transition-colors last:border-b-0',
                            expanded && 'bg-[#f5f8ff]',
                            !hasRowCheckbox(row, activeBucket) && 'opacity-70',
                          )}
                        >
                          <div className="grid grid-cols-[24px_36px_minmax(0,1fr)_auto] items-center gap-3 md:grid-cols-[24px_40px_minmax(0,1fr)_90px_110px_120px]">
                            <input
                              type="checkbox"
                              checked={selectedKeys.has(selectionKey)}
                              disabled={!hasRowCheckbox(row, activeBucket)}
                              onChange={(event) =>
                                setSelectedKeys((current) => {
                                  const next = new Set(current)
                                  if (event.target.checked) next.add(selectionKey)
                                  else next.delete(selectionKey)
                                  return next
                                })
                              }
                              aria-label={`Select ${recordName(liberty)}`}
                            />
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#dce7ff] to-[#eef3ff] text-xs font-bold text-[#3151d3]">
                              {initials(recordName(liberty))}
                            </div>
                            <div className="min-w-0">
                              <strong className="block truncate text-sm text-[#16213e]">{recordName(liberty)}</strong>
                              <p className="truncate text-[11px] leading-5 text-[#70809b]">{String(liberty.user_email ?? liberty.email ?? '--')}</p>
                              <p className="truncate text-[11px] leading-5 text-[#70809b]">
                                Unit {String(liberty.unit_number ?? liberty.unit ?? '--')} | Lease {displayDate(liberty.lease_start_date)}
                              </p>
                            </div>
                            <div className="col-start-3 text-left md:col-auto md:text-right">
                              {score != null ? (
                                <>
                                  <strong className="block text-[15px] text-[#16213e]">{String(score)}</strong>
                                  <small className="text-[11px] capitalize text-[#70809b]">{confidence(row) === 'none' ? 'No Match' : confidence(row)} confidence</small>
                                </>
                              ) : null}
                            </div>
                            <div className="col-start-3 text-left md:col-auto md:text-right">
                              <span className="inline-block whitespace-nowrap rounded-full bg-[#ecf3ff] px-2.5 py-1 text-[10px] font-bold text-[#3151d3]">
                                {decisionLabel(row)}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedKey((current) => nextYardiReviewExpandedKey(current, row, index))
                              }
                              className="col-start-3 inline-flex items-center justify-center gap-2 rounded-md border border-[#d5e0f1] bg-white px-3 py-2 text-xs font-semibold text-[#51607a] md:col-auto"
                              aria-label={expanded ? 'Close review' : 'Review resident'}
                            >
                              <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
                              {expanded ? 'Close Review' : 'Review'}
                            </button>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div className="flex min-h-[240px] items-center justify-center p-6 text-xs text-[#70809b]">
                      {activeRows.length
                        ? 'No residents match your current search or filter.'
                        : 'No residents found in this review bucket.'}
                    </div>
                  )}
                </div>

                {expandedRow ? (
                  <aside className="fixed inset-2 z-[10030] overflow-y-auto rounded-[20px] border border-[#e4ebf7] bg-white p-4 shadow-2xl md:static md:z-auto md:ml-4 md:min-w-[320px] md:basis-[60%] md:bg-white/95 md:shadow-none lg:min-w-[360px] xl:min-w-[420px]">
                    <div className="mb-3 flex items-start justify-between">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">Detailed Review</p>
                        <h3 className="mt-0.5 text-lg font-bold text-[#16213e]">{recordName(libertyRecord(expandedRow))}</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpandedKey(null)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-[#d5e0f1] bg-white"
                        aria-label="Close detailed review"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_72px_minmax(0,1fr)] items-center gap-4">
                      <ResidentDetailCard row={expandedRow} />
                      <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full border border-[#dce6fb] bg-gradient-to-br from-[#e6efff] to-white px-2 text-center text-[10px] font-bold text-[#3151d3]">
                        Best Yardi Match
                      </div>
                      <ResidentDetailCard row={expandedRow} source />
                    </div>
                    {activeBucket !== 'skipped' ? <MatchEvidence row={expandedRow} /> : null}
                    {activeBucket === 'skipped' && (expandedRow.reason || expandedRow.message) ? (
                      <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        {String(expandedRow.reason ?? expandedRow.message)}
                      </p>
                    ) : null}
                  </aside>
                ) : null}
              </div>
            </div>
          </div>
          </div>
        </div>
      ) : null}
      <ConfirmDialog
        isOpen={submitConfirmOpen}
        title="Map Residents"
        message={`Are you sure you want to insert ${selectedKeys.size} selected resident ${selectedKeys.size === 1 ? 'mapping' : 'mappings'} to Yardi?`}
        primaryLabel="Yes"
        secondaryLabel="Cancel"
        onPrimary={() => {
          setSubmitConfirmOpen(false)
          void submitMappings()
        }}
        onSecondary={() => setSubmitConfirmOpen(false)}
        {...companyEditConfirmPresentation}
      />
    </>
  )
}
